import type { TObjectTypePermissions } from "types/authorization";
import type { TTicketDuration } from "utils/durationsOptions";

import { compiledClientConfig } from "config";
import { List, Record } from "immutable";
import { ApplicationModel } from "models/ApplicationModel";
import { ApprovalAlgorithmModel } from "models/ApprovalAlgorithmModel";
import { getMaintainerModels } from "utils/maintainers";

const ENTITLE_ADAPTER_NAME = "[ENTITLE]";

const defaults = {
  adapterless: false,
  adapterName: null as null | string,
  agentTokenId: null as null | string,
  allowActorsSelfMapping: false,
  allowedDurations: null as List<TTicketDuration> | null,
  application: null as ApplicationModel | null,
  applicationId: null as null | string,
  auditLogsCount: 0,
  autoAssignRecommendedResourceMaintainers: false,
  autoAssignRecommendedResourceOwner: false,
  canCreateActors: false as boolean,
  canEditPermissions: true as boolean,
  configuration: null as null | { [key: string]: string },
  createdAt: null as Date | null,
  defaultApprovalAlgorithm: new ApprovalAlgorithmModel(),
  defaultRequestable: false as boolean,
  eligible: false,
  id: "",
  imageUrl: null as null | string,
  isDeleted: false,
  isOwnerDeleted: false as boolean,
  lastFullSync: null as Date | null,
  lastResourcesSync: null as Date | null,
  lastResourcesSyncError: null as Date | null,
  maintainerGroupId: null as null | string,
  maintainers: null as List<TMaintainerModel> | null,
  manual: false,
  name: "",
  nextSyncTime: new Date(),
  notifyAboutExternalPermissions: false,
  oauthConfiguration: false as boolean,
  ownerFullName: "",
  ownerId: "",
  permissions: null as null | TObjectTypePermissions<"integration">,
  prerequisitePermissionId: null as null | string,
  readonly: false,
  requestable: true,
  resourcesCount: 0,
  resourcesSyncIntervalInMinutes: 60,
  syncLimitInMinutes: 0,
  virtual: false,
};

export type TIntegrationModel = typeof defaults;

export class IntegrationModel extends Record<typeof defaults>(defaults) {
  get actorless() {
    return this.canCreateActors && !this.canEditPermissions;
  }

  get isEntitle() {
    return this.adapterName === ENTITLE_ADAPTER_NAME;
  }

  static fromServerData(data: unknown) {
    const {
      adapterless,
      adapterName,
      agentTokenId,
      allowActorsSelfMapping,
      allowedDurations,
      application,
      applicationId,
      auditLogsCount,
      autoAssignRecommendedResourceMaintainers,
      autoAssignRecommendedResourceOwner,
      canCreateActors,
      canEditPermissions,
      configuration,
      createdAt,
      defaultApprovalAlgorithm,
      defaultRequestable,
      eligible,
      id,
      imageUrl,
      isDeleted,
      isOwnerDeleted,
      lastFullSync,
      lastResourcesSync,
      lastResourcesSyncError,
      maintainerGroupId,
      maintainers,
      manual,
      name,
      nextSyncTime,
      notifyAboutExternalPermissions,
      oauthConfiguration,
      ownerFullName,
      ownerId,
      permissions,
      prerequisitePermissionId,
      readonly,
      requestable,
      resourcesCount,
      resourcesSyncIntervalInMinutes,
      syncLimitInMinutes,
      virtual,
    } = data as typeof defaults;
    const modelApplication = application ? ApplicationModel.fromServerData(application) : null;
    return new IntegrationModel({
      adapterless,
      adapterName: adapterName || null,
      agentTokenId: agentTokenId || null,
      allowActorsSelfMapping,
      allowedDurations: allowedDurations ? List(allowedDurations) : null,
      application: modelApplication,
      applicationId,
      auditLogsCount: Number(auditLogsCount),
      autoAssignRecommendedResourceMaintainers,
      autoAssignRecommendedResourceOwner,
      canCreateActors,
      canEditPermissions,
      configuration,
      createdAt: createdAt ? new Date(createdAt) : null,
      defaultApprovalAlgorithm: defaultApprovalAlgorithm
        ? ApprovalAlgorithmModel.fromServerData(defaultApprovalAlgorithm)
        : undefined,
      defaultRequestable,
      eligible,
      id,
      imageUrl:
        compiledClientConfig.spog && imageUrl ? `/${compiledClientConfig.basePrefix}` + imageUrl : imageUrl || null,
      isDeleted,
      isOwnerDeleted,
      lastFullSync: lastFullSync ? new Date(lastFullSync) : null,
      lastResourcesSync: lastResourcesSync ? new Date(lastResourcesSync) : null,
      lastResourcesSyncError: lastResourcesSyncError ? new Date(lastResourcesSyncError) : null,
      maintainerGroupId,
      maintainers: getMaintainerModels(maintainers),
      manual,
      name,
      nextSyncTime: new Date(nextSyncTime),
      notifyAboutExternalPermissions,
      oauthConfiguration,
      ownerFullName,
      ownerId,
      permissions: permissions ?? ({} as TObjectTypePermissions<"integration">),
      prerequisitePermissionId,
      readonly,
      requestable,
      resourcesCount: Number(resourcesCount),
      resourcesSyncIntervalInMinutes,
      syncLimitInMinutes,
      virtual,
    });
  }
}
